import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Popover,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Manage_Submenu_Hierarchical } from "./adminMenu";

const HierarchicalManageMenu = ({
  open,
  onClose,
  handleClickMenuWithlabel,
  style,
  isIconOnly = false,
  activeItemKey,
  Location,
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  const toggleCategory = (categoryKey, event) => {
    if (expandedCategories[categoryKey]) {
      // Close submenu
      setExpandedCategories((prev) => ({
        ...prev,
        [categoryKey]: false,
      }));
      setSubmenuAnchor(null);
      setActiveCategory(null);
    } else {
      // Open submenu
      setExpandedCategories((prev) => ({
        ...prev,
        [categoryKey]: true,
      }));
      setSubmenuAnchor(event.currentTarget);
      setActiveCategory(categoryKey);
    }
  };

  const handleMenuItemClick = (path) => {
    handleClickMenuWithlabel(path);
    setExpandedCategories({});
    setSubmenuAnchor(null);
    setActiveCategory(null);
  };

  const handleMainMenuClose = () => {
    onClose();
    setExpandedCategories({});
    setSubmenuAnchor(null);
    setActiveCategory(null);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setActiveCategory(null);
    setExpandedCategories({});
  };

  return (
    <>
      <Menu
        slotProps={{
          paper: {
            sx: {
              width: "170px",
              maxHeight: "490px",
              overflowY: "visible",
              overflow: "visible",
              borderRadius: "12px", // Added border radius to menu paper
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
            },
          },
        }}
        open={open}
        onClose={handleMainMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
          sx: { padding: 0 },
        }}
        style={style}
        className="sidebar_menu"
        sx={{
          "& .MuiMenuItem-root": {
            gap: 1,
            minHeight: "40px",
            margin: "2px 4px",
          },
        }}
      >
        {Object.entries(Manage_Submenu_Hierarchical).map(
          ([categoryKey, category]) => {
            // Special styling for configuration when expanded
            const isConfigurationCategory = categoryKey === "configuration";
            const isExpanded = expandedCategories[categoryKey];

            return (
              <Box key={categoryKey}>
                {category.isExpandable ? (
                  /* Expandable Category */
                  <MenuItem
                    onClick={(event) => toggleCategory(categoryKey, event)}
                    sx={{
                      backgroundColor: isExpanded ? "#404940" : "#404940",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderBottom: "1px solid #555",
                      borderRadius:
                        isConfigurationCategory && isExpanded ? "20px" : "8px",
                      width:
                        isConfigurationCategory && isExpanded
                          ? "175px"
                          : "165px",
                      position: "relative",

                      overflow: "visible",
                      "&:hover": {
                        backgroundColor: "#404940",
                        borderRadius: "8px", // Maintain border radius on hover
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      {category.icon}
                      <Typography
                        sx={{
                          marginLeft: "8px", // Increased margin for better spacing
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        {category.title}
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : (
                  /* Non-expandable category */
                  <MenuItem
                    onClick={() => handleMenuItemClick(category.path)}
                    sx={{
                      fontSize: "14px",
                      borderBottom: "1px solid #555",
                      width: "170px",
                      borderRadius: "8px", // Added border radius
                      "&:hover": {
                        backgroundColor: "#4D574C",
                        borderRadius: "8px", // Maintain border radius on hover
                      },
                    }}
                  >
                    {category.icon}
                    <Typography
                      sx={{
                        marginLeft: "8px", // Consistent margin
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {category.title}
                    </Typography>
                  </MenuItem>
                )}
              </Box>
            );
          }
        )}
      </Menu>

      {/* Right-side Submenu */}
      <Popover
        open={Boolean(submenuAnchor) && Boolean(activeCategory)}
        anchorEl={submenuAnchor}
        onClose={handleSubmenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              width: "214px",
              maxHeight: "400px",
              overflowY: "auto",
              marginLeft: "2px",
              borderRadius: "12px", // Added border radius to popover paper
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
            },
          },
        }}
        sx={{
          "& .MuiList-root": {
            background: "#3D4740",
            opacity: 0.95,
            color: "white",
            padding: 0,
            borderRadius: "12px",
          },
        }}
      >
        <List
          sx={{
            padding: 0,
            backgroundColor: "#3D4740",
            borderRadius: "12px", // Added border radius to list
          }}
        >
          {activeCategory &&
            Manage_Submenu_Hierarchical[activeCategory]?.items?.map(
              (item, index, array) => (
                <ListItem key={item.key} sx={{ padding: 0 }}>
                  <ListItemButton
                    onClick={() => handleMenuItemClick(item.path)}
                    sx={{
                      paddingLeft: "16px",
                      paddingRight: "16px",
                      paddingY: "8px",
                      minHeight: "36px",
                      margin: "2px 4px", // Added margin for visual separation
                      borderRadius: "8px", // Added border radius to list item buttons
                      borderBottom: "1px solid #555",

                      "&:hover": {
                        backgroundColor: "#4D574C",
                        borderRadius: "8px", // Maintain border radius on hover
                      },
                      // Special border radius for first and last items
                      ...(index === 0 && {
                        borderTopLeftRadius: "12px",
                        borderTopRightRadius: "12px",
                        marginTop: "4px",
                      }),
                      ...(index === array.length - 1 && {
                        borderBottomLeftRadius: "12px",
                        borderBottomRightRadius: "12px",
                        marginBottom: "4px",
                      }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "32px",
                        color: "white",
                        borderRadius: "4px", // Added subtle border radius to icons
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        className: "normal__normal__600__12__Manrope",
                      }}
                      primary={item.label}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "13px",
                          color: "white",
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            )}
        </List>
      </Popover>
    </>
  );
};

export default HierarchicalManageMenu;
